"use client";

import type { GameSearchResult, PublisherSearchResult } from "./types";
import { GamesResultsSection } from "./GamesResultsSection";
import { PublishersResultsSection } from "./PublishersResultsSection";
import { MIN_QUERY_LENGTH } from "./constants";

interface SearchResultsDropdownProps {
  query: string;
  loading: boolean;
  games: GameSearchResult[];
  publishers: PublisherSearchResult[];
  expandedGames: boolean;
  expandedPublishers: boolean;
  onToggleGamesExpand: () => void;
  onTogglePublishersExpand: () => void;
  onSelect: () => void;
}

export function SearchResultsDropdown({
  query,
  loading,
  games,
  publishers,
  expandedGames,
  expandedPublishers,
  onToggleGamesExpand,
  onTogglePublishersExpand,
  onSelect,
}: SearchResultsDropdownProps) {
  const trimmed = query.trim();
  const hasResults = games.length > 0 || publishers.length > 0;
  const isNumericId = Number.isFinite(Number(trimmed)) && Number(trimmed) > 0;
  const isQueryActive = trimmed.length >= MIN_QUERY_LENGTH || isNumericId;
  const showEmptyState = isQueryActive && !loading && !hasResults;

  if (!hasResults && !showEmptyState) return null;

  return (
    <div className="win-scrollbar absolute z-20 mt-1 w-full max-h-[calc(100vh-var(--header-height)-8px)] sm:max-h-[600px] overflow-y-auto rounded-lg border border-[#2a475e] bg-[#1b2838] shadow-2xl backdrop-blur-sm animate-slide-in">
      {hasResults ? (
        <>
          <GamesResultsSection
            games={games}
            query={trimmed}
            isExpanded={expandedGames}
            onToggleExpand={onToggleGamesExpand}
            onSelect={onSelect}
          />
          <PublishersResultsSection
            publishers={publishers}
            isExpanded={expandedPublishers}
            onToggleExpand={onTogglePublishersExpand}
            onSelect={onSelect}
          />
        </>
      ) : (
        <div className="px-4 py-6 text-center text-sm text-[#8f98a0]">
          Nothing found for{" "}
          <span className="font-medium text-white">
            &ldquo;{trimmed}&rdquo;
          </span>
          .
          <div className="mt-1 text-xs text-[#67707a]">
            Try a different spelling or fewer characters.
          </div>
        </div>
      )}
    </div>
  );
}
